import { useEffect, useState } from "react";
import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/th";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import Alert from "@mui/material/Alert";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import {
  extendPricebookPeriod,
  readPricebookPeriodRefusal,
} from "../../../../services";
import { PricebookPeriod } from "../../../../types/price-book.type";
import ConfirmModal from "../../../ConfirmModal";
import {
  formatPeriodDay,
  formatPeriodRange,
  validateExtendDate,
} from "./periodModel";

const PICKER_FORMAT = "DD/MM/YYYY";
const EXTEND_FAILED = "ต่อช่วงราคาไม่สำเร็จ กรุณาลองอีกครั้ง";

/** The period to extend and the book it belongs to (the overview lists periods of every book). */
export interface ExtendPeriodTarget {
  pricebookUuid: string;
  period: PricebookPeriod;
}

interface ExtendPeriodDialogProps {
  /** Null while the dialog is closed. */
  target: ExtendPeriodTarget | null;
  onClose: () => void;
  /** The new last day was saved: the caller closes the dialog and reads the periods again. */
  onExtended: () => void;
}

/** ต่อช่วง: moves a period's last day later. Its prices stay as they are. */
export default function ExtendPeriodDialog({
  target,
  onClose,
  onExtended,
}: ExtendPeriodDialogProps) {
  const [endDate, setEndDate] = useState<Dayjs | null>(null);
  const [error, setError] = useState<string>("");
  const [saving, setSaving] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");

  useEffect(() => {
    if (!target) return;
    setEndDate(null);
    setError("");
    setErrorMessage("");
  }, [target]);

  const period = target?.period;
  const firstAllowedDay = period ? dayjs(period.end_date).add(1, "day") : undefined;
  const title = period
    ? `ต่อช่วงราคา ${period.product_code} ${period.product_name ?? ""}`
    : "";

  const handleChangeEndDate = (picked: Dayjs | null) => {
    setEndDate(picked);
    setError("");
  };

  const handleClose = () => {
    if (saving) return;
    onClose();
  };

  const handleExtend = async () => {
    if (!target) return;
    const invalid = validateExtendDate(endDate, target.period.end_date);
    if (invalid) {
      setError(invalid);
      return;
    }
    // validateExtendDate already refused an empty day; this only narrows the type.
    if (!endDate) return;
    setSaving(true);
    setErrorMessage("");
    try {
      await extendPricebookPeriod(
        target.pricebookUuid,
        target.period.uuid,
        formatPeriodDay(endDate),
      );
      onExtended();
    } catch (refusal) {
      setErrorMessage(readPricebookPeriodRefusal(refusal) ?? EXTEND_FAILED);
    } finally {
      setSaving(false);
    }
  };

  return (
    <ConfirmModal
      open={Boolean(target)}
      title={title}
      showCloseButton
      confirmLabel="ต่อช่วง"
      loading={saving}
      onClose={handleClose}
      onConfirm={handleExtend}
    >
      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="th">
        <Stack gap={2} sx={{ pt: 1 }}>
          {period && (
            <Typography variant="body2" color="text.secondary">
              ช่วงเดิม {formatPeriodRange(period.start_date, period.end_date)} ·
              ราคาในช่วงคงเดิม
            </Typography>
          )}
          <DatePicker
            label="วันสิ้นสุดใหม่"
            value={endDate}
            format={PICKER_FORMAT}
            minDate={firstAllowedDay}
            onChange={handleChangeEndDate}
            slotProps={{
              textField: {
                size: "small",
                fullWidth: true,
                error: Boolean(error),
                helperText: error,
              },
            }}
          />
          {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
        </Stack>
      </LocalizationProvider>
    </ConfirmModal>
  );
}
